import { Component } from "react";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import OrderCell from "../common/orderCell";
import QualityAttributeBadges from "../common/qualityAttributeBadges";
import { withTranslation } from "react-i18next";
import _ from "lodash";

class MarketGrid extends Component {
  state = {
    gridData: [],
    expandedInstruments: {}, // instrumentId => boolean
  };

  normalizeSortValue = (value) => String(value ?? "").toLocaleLowerCase();

  compareSortValues = (left, right) =>
    this.normalizeSortValue(left).localeCompare(this.normalizeSortValue(right), undefined, {
      numeric: true,
      sensitivity: "base",
    });

  getOrderedQualityAttributeValues = (qualityAttributeValues = []) =>
    [...(qualityAttributeValues || [])].sort((left, right) =>
      String(left?.qualityAttributeValueId ?? "").localeCompare(
        String(right?.qualityAttributeValueId ?? ""),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        },
      ),
    );

  buildQualityAttributeSortKey = (qualityAttributeValues = []) =>
    this.getOrderedQualityAttributeValues(qualityAttributeValues)
      .map((qualityAttributeValue) => qualityAttributeValue?.value ?? "")
      .join("|");

  async componentDidMount() {
    this.prepareMarketGrid();
  }

  async componentDidUpdate(previousProps) {
    if (
      !_.isEqual(previousProps.marketInstruments, this.props.marketInstruments) ||
      !_.isEqual(previousProps.marketOrders, this.props.marketOrders) ||
      !_.isEqual(
        previousProps.organizationOrders,
        this.props.organizationOrders,
      ) ||
      !_.isEqual(
        previousProps.userOrganizationIds,
        this.props.userOrganizationIds,
      ) ||
      !_.isEqual(previousProps.marketControl, this.props.marketControl)
    ) {
      this.prepareMarketGrid();
    }
  }

  getCanPickForMarket = (marketId) => {
    const marketControl = this.props.marketControl.find(
      (market) => market.marketId === marketId,
    );

    if (!marketControl?.allowPick) {
      return false;
    }

    const associatedOrganizationIds = marketControl.organizationIds || [];
    if (!associatedOrganizationIds.length) {
      return false;
    }

    if (this.props.tradeOnBehalf) {
      return true;
    }

    return associatedOrganizationIds.some((organizationId) =>
      this.props.userOrganizationIds.includes(organizationId),
    );
  };

  toggleRowExpand = (instrumentId) => {
    this.setState(
      (prevState) => ({
        expandedInstruments: {
          ...prevState.expandedInstruments,
          [instrumentId]: !prevState.expandedInstruments[instrumentId],
        },
      }),
      () => {
        if (this.gridApi) {
          this.gridApi.onFilterChanged();
        }
      },
    );
  };

  getInstrumentMetadata = (bid, offer, instrument) => {
    const primary = bid || offer || instrument || {};

    return {
      instrumentId: primary.instrumentId,
      termId: primary.termId,
      termName: primary.termName,
      marketName: primary.marketName,
      marketId: primary.marketId,
      uomId: primary.uomId,
      uomName: primary.uomName,
      currencyId: primary.currencyId,
      currencyName: primary.currencyName,
      currencySymbol: primary.currencySymbol ?? "$",
      volumePrecision: primary.volumePrecision ?? 0,
      pricePrecision: primary.pricePrecision ?? 2,
      priceBasisId: primary.priceBasisId,
      priceBasisName: primary.priceBasisName,
      qualityAttributeValues: primary.qualityAttributeValues ?? [],
    };
  };

  buildSelectionData = (type, rowData) => {
    const instrument = this.props.marketInstruments.find(
      (candidate) => candidate.instrumentId === rowData?.instrumentId,
    ) || {};
    const metadata = this.getInstrumentMetadata(null, null, instrument);
    const isBidSelection = type === "Bid";

    return {
      instrumentId: rowData?.instrumentId ?? metadata.instrumentId,
      marketId: rowData?.marketId ?? metadata.marketId,
      marketName: rowData?.marketName ?? metadata.marketName,
      termId: rowData?.termId ?? metadata.termId,
      termName: rowData?.termName ?? metadata.termName,
      priceBasisId: rowData?.priceBasisId ?? metadata.priceBasisId,
      priceBasisName: rowData?.priceBasisName ?? metadata.priceBasisName,
      currencyId: rowData?.currencyId ?? metadata.currencyId,
      currencyName: rowData?.currencyName ?? metadata.currencyName,
      currencySymbol: rowData?.currencySymbol ?? metadata.currencySymbol,
      uomId: rowData?.uomId ?? metadata.uomId,
      uomName: rowData?.uomName ?? metadata.uomName,
      pricePrecision: rowData?.pricePrecision ?? metadata.pricePrecision,
      volumePrecision: rowData?.volumePrecision ?? metadata.volumePrecision,
      qualityAttributeValues:
        rowData?.qualityAttributeValues ?? metadata.qualityAttributeValues ?? [],
      bidPrice: isBidSelection ? (rowData?.bidPrice ?? null) : null,
      offerPrice: isBidSelection ? null : rowData?.offerPrice ?? null,
      bidVolume: isBidSelection ? (rowData?.bidVolume ?? null) : null,
      offerVolume: isBidSelection ? null : rowData?.offerVolume ?? null,
      runningTotalBidVolume: isBidSelection
        ? rowData?.runningTotalBidVolume ?? rowData?.bidVolume ?? null
        : null,
      runningTotalOfferVolume: isBidSelection
        ? null
        : rowData?.runningTotalOfferVolume ?? rowData?.offerVolume ?? null,
    };
  };

  handleEmptyCellClick = (type, rowData) => {
    this.props.onOrderClicked(type, this.buildSelectionData(type, rowData));
  };

  renderEmptyOrderCell = (type, params) => {
    const isBid = type === "Bid";
    const disabled = params.data?.isExpired;

    return (
      <div
        className={`${disabled ? "" : "clickable"} market-grid-order-cell ${
          isBid ? "is-bid" : "is-offer"
        } ${params.data?.queueOrder > 0 ? "is-depth" : ""} ${
          disabled ? "is-disabled" : ""
        } market-grid-order-cell-empty`}
        onClick={() => !disabled && this.handleEmptyCellClick(type, params.data)}
        style={{
          justifyContent: isBid ? "flex-end" : "flex-start",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <span className="market-grid-order-empty-hitbox" />
      </div>
    );
  };

  renderOrderSideCell = ({
    params,
    type,
    orderId,
    displayValue,
    price,
    volume,
    isOrganizationOrder,
    isUserOrder,
    superscript,
    contentType,
  }) => {
    if (!orderId) {
      return this.renderEmptyOrderCell(type, params);
    }

    return (
      <OrderCell
        value={displayValue}
        type={type}
        isOrganizationOrder={isOrganizationOrder}
        isUserOrder={isUserOrder}
        isDepth={params.data?.queueOrder > 0}
        pricePrecision={params.data?.pricePrecision}
        volumePrecision={params.data?.volumePrecision}
        currencyName={params.data?.currencyName}
        currencySymbol={params.data?.currencySymbol}
        uomName={params.data?.uomName}
        data={params.data}
        selectionData={this.buildSelectionData(type, params.data)}
        price={price}
        volume={volume}
        disabled={params.data?.isExpired}
        instrumentId={params.data?.instrumentId}
        orderId={orderId}
        onOrderClicked={this.props.onOrderClicked}
        showContextMenu={true}
        modifyOrder={this.props.modifyOrder}
        withdrawOrder={this.props.withdrawOrder}
        pickOrder={this.props.pickOrder}
        tradeOnBehalf={this.props.tradeOnBehalf}
        tradeAllowed={this.props.tradeAllowed}
        align={true}
        superscript={superscript}
        allowPick={this.getCanPickForMarket(params.data?.marketId)}
        displayMode="marketGridBadge"
        contentType={contentType}
      />
    );
  };

  columnDefs = [
    {
      field: "marketNameDisplay",
      headerName: this.props.t("MarketName"),
      filter: false,
      sortable: false,
      flex: 2,
    },
    {
      field: "termNameDisplay",
      headerName: this.props.t("TermName"),
      filter: false,
      sortable: false,
      flex: 1.5,
    },
    {
      field: "qualityAttributeValues",
      headerName: this.props.t("QualityAttributes"),
      flex: 1.5,
      filter: false,
      sortable: false,
      cellRenderer: (params) => (
        <QualityAttributeBadges
          qualityAttributeValues={params.data?.qualityAttributeValuesDisplay}
        />
      ),
    },
    {
      field: "expandDepth",
      headerName: "",
      filter: false,
      sortable: false,
      width: 20,
      cellClass: "ag-center-aligned-cell",
      cellRenderer: (params) => (
        <span>
          {params.data?.queueOrder === 0 && params.data?.hasDepth && (
            <span>
              <input
                type="checkbox"
                className="queue-toggle"
                id={params.data?.instrumentId}
                onChange={() => this.toggleRowExpand(params.data?.instrumentId)}
                hidden
              />
              <label
                htmlFor={params.data?.instrumentId}
                className="queue-toggle-label"
              ></label>
            </span>
          )}
        </span>
      ),
    },
    {
      field: "bidVolume",
      headerName: this.props.t("BidVolume"),
      filter: false,
      sortable: false,
      flex: 1,
      cellRenderer: (params) =>
        this.renderOrderSideCell({
          params,
          type: "Bid",
          orderId: params.data?.bidOrderId,
          displayValue: params.data?.bidVolume,
          price: params.data?.bidPrice,
          volume: params.data?.bidVolume,
          isOrganizationOrder: params.data?.isOrganizationBid,
          isUserOrder: params.data?.isUserBid,
          superscript: params.data?.bidAllOrNone && "aon",
          contentType: "volume",
        }),
    },
    {
      field: "bidPrice",
      headerName: this.props.t("BidPrice"),
      filter: false,
      sortable: false,
      flex: 1,
      cellRenderer: (params) =>
        this.renderOrderSideCell({
          params,
          type: "Bid",
          orderId: params.data?.bidOrderId,
          displayValue: params.data?.bidPrice,
          price: params.data?.bidPrice,
          volume: params.data?.bidVolume,
          isOrganizationOrder: params.data?.isOrganizationBid,
          isUserOrder: params.data?.isUserBid,
          contentType: "price",
        }),
    },
    {
      field: "offerPrice",
      headerName: this.props.t("OfferPrice"),
      filter: false,
      sortable: false,
      flex: 1,
      cellRenderer: (params) =>
        this.renderOrderSideCell({
          params,
          type: "Offer",
          orderId: params.data?.offerOrderId,
          displayValue: params.data?.offerPrice,
          price: params.data?.offerPrice,
          volume: params.data?.offerVolume,
          isOrganizationOrder: params.data?.isOrganizationOffer,
          isUserOrder: params.data?.isUserOffer,
          contentType: "price",
        }),
    },
    {
      field: "offerVolume",
      headerName: this.props.t("OfferVolume"),
      filter: false,
      sortable: false,
      flex: 1,
      cellRenderer: (params) =>
        this.renderOrderSideCell({
          params,
          type: "Offer",
          orderId: params.data?.offerOrderId,
          displayValue: params.data?.offerVolume,
          price: params.data?.offerPrice,
          volume: params.data?.offerVolume,
          isOrganizationOrder: params.data?.isOrganizationOffer,
          isUserOrder: params.data?.isUserOffer,
          superscript: params.data?.offerAllOrNone && "aon",
          contentType: "volume",
        }),
    },
  ];

  buildInstrumentRow = ({
    bid,
    offer,
    instrument,
    index,
    hasDepth,
    runningTotalBidVolume,
    runningTotalOfferVolume,
    organizationOrders,
    userAccountId,
  }) => {
    const metadata = this.getInstrumentMetadata(bid, offer, instrument);

    const organizationBidIndex = organizationOrders.findIndex(
      (candidate) => candidate.orderId === (bid ? bid.orderId : undefined),
    );
    const isOrganizationBid = organizationBidIndex >= 0;
    const isUserBid = isOrganizationBid
      ? organizationOrders[organizationBidIndex].userAccountId === userAccountId
      : false;
    const organizationOfferIndex = organizationOrders.findIndex(
      (candidate) => candidate.orderId === (offer ? offer.orderId : undefined),
    );
    const isOrganizationOffer = organizationOfferIndex >= 0;
    const isUserOffer = isOrganizationOffer
      ? organizationOrders[organizationOfferIndex].userAccountId === userAccountId
      : false;

    return {
      ...metadata,
      termNameDisplay: index === 0 ? metadata.termName : undefined,
      marketNameDisplay: index === 0 ? metadata.marketName : undefined,
      qualityAttributeValuesDisplay:
        index === 0 ? metadata.qualityAttributeValues : undefined,
      bidOrderId: bid ? bid.orderId : undefined,
      bidPrice: bid ? bid.price : undefined,
      bidVolume: bid ? bid.volume : undefined,
      bidAllOrNone: bid ? bid.allOrNone : undefined,
      runningTotalBidVolume:
        runningTotalBidVolume === 0 ? undefined : runningTotalBidVolume,
      isOrganizationBid,
      isUserBid,
      bidOrderDate: bid ? bid.orderDate : undefined,
      offerOrderId: offer ? offer.orderId : undefined,
      offerPrice: offer ? offer.price : undefined,
      offerVolume: offer ? offer.volume : undefined,
      offerAllOrNone: offer ? offer.allOrNone : undefined,
      runningTotalOfferVolume:
        runningTotalOfferVolume === 0 ? undefined : runningTotalOfferVolume,
      isOrganizationOffer,
      isUserOffer,
      offerOrderDate: offer ? offer.orderDate : undefined,
      queueOrder: index,
      rowId: `${metadata.instrumentId}|${index}`,
      hasDepth,
      isExpired: instrument?.isExpired ?? false,
    };
  };

  buildInstrumentRows = (
    orders,
    instrument,
    organizationOrders,
    userAccountId,
  ) => {
    if (!orders.length) {
      return [
        this.buildInstrumentRow({
          bid: null,
          offer: null,
          instrument,
          index: 0,
          hasDepth: false,
          runningTotalBidVolume: 0,
          runningTotalOfferVolume: 0,
          organizationOrders,
          userAccountId,
        }),
      ];
    }

    const buyOrders = orders.filter((order) => order.orderDirection === "Buy");
    const sellOrders = orders.filter((order) => order.orderDirection === "Sell");

    const sortedBids = _.orderBy(
      buyOrders,
      ["price", "orderDate"],
      ["desc", "asc"],
    );

    const sortedOffers = _.orderBy(
      sellOrders,
      ["price", "orderDate"],
      ["asc", "asc"],
    );

    const pairings = _.zip(sortedBids, sortedOffers).map((pair) => ({
      bid: pair[0],
      offer: pair[1],
    }));

    let runningTotalBidVolume = 0;
    let runningTotalOfferVolume = 0;
    const hasDepth = pairings.length > 1;

    return pairings.map((pairing, index) => {
      runningTotalBidVolume += pairing.bid ? pairing.bid.volume : 0;
      runningTotalOfferVolume += pairing.offer ? pairing.offer.volume : 0;

      return this.buildInstrumentRow({
        bid: pairing.bid,
        offer: pairing.offer,
        instrument,
        index,
        hasDepth,
        runningTotalBidVolume,
        runningTotalOfferVolume,
        organizationOrders,
        userAccountId,
      });
    });
  };

  compareInstrumentIds = (left, right) => {
    return String(left ?? "").localeCompare(String(right ?? ""), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  compareGridRows = (left, right) => {
    const comparisons = [
      this.compareSortValues(left.marketNameSortKey, right.marketNameSortKey),
      this.compareSortValues(left.termNameSortKey, right.termNameSortKey),
      this.compareSortValues(
        left.qualityAttributeSortKey,
        right.qualityAttributeSortKey,
      ),
      this.compareSortValues(
        left.priceBasisName ?? "",
        right.priceBasisName ?? "",
      ),
      this.compareSortValues(left.currencyName ?? "", right.currencyName ?? ""),
      this.compareSortValues(left.uomName ?? "", right.uomName ?? ""),
      this.compareInstrumentIds(left.instrumentId, right.instrumentId),
    ];

    const firstNonZeroComparison = comparisons.find(
      (comparison) => comparison !== 0,
    );

    if (firstNonZeroComparison) {
      return firstNonZeroComparison;
    }

    return (left.queueOrder ?? 0) - (right.queueOrder ?? 0);
  };

  markMarketGroupBoundaries = (gridData = []) => {
    let previousTopLevelMarketId = null;

    return gridData.map((row) => {
      if ((row.queueOrder ?? 0) > 0) {
        return {
          ...row,
          showMarketDivider: false,
        };
      }

      const showMarketDivider =
        previousTopLevelMarketId !== null &&
        String(previousTopLevelMarketId) !== String(row.marketId);

      previousTopLevelMarketId = row.marketId;

      return {
        ...row,
        showMarketDivider,
      };
    });
  };

  prepareMarketGrid() {
    const {
      marketInstruments = [],
      marketOrders = [],
      organizationOrders = [],
      userAccountId,
    } = this.props;

    const instrumentsById = _.keyBy(marketInstruments, "instrumentId");
    const filteredMarketOrders = marketOrders.filter((order) =>
      Boolean(instrumentsById[order.instrumentId]),
    );
    const groupedOrders = _.groupBy(filteredMarketOrders, "instrumentId");
    const instrumentIds = marketInstruments
      .map((instrument) => instrument.instrumentId)
      .filter(Boolean);

    const gridData = this.markMarketGroupBoundaries(
      _.flatMap(instrumentIds, (instrumentId) =>
        this.buildInstrumentRows(
          groupedOrders[instrumentId] || [],
          instrumentsById[instrumentId],
          organizationOrders,
          userAccountId,
        ),
      )
        .map((row) => ({
          ...row,
          marketNameSortKey: row.marketName ?? "",
          termNameSortKey: row.termName ?? "",
          qualityAttributeSortKey: this.buildQualityAttributeSortKey(
            row.qualityAttributeValues,
          ),
        }))
        .sort(this.compareGridRows),
    );

    this.setState({ gridData });
  }

  isFilterActive = () => true;

  doesExternalFilterPass = (node) => {
    const { instrumentId, queueOrder } = node.data;
    const isExpanded = this.state.expandedInstruments[instrumentId];
    return queueOrder === 0 || isExpanded;
  };

  render() {
    const { gridData } = this.state;

    return (
      <ThemeableAgGrid
        className="table table-bold table-small market-grid trading-grid"
        columnDefs={this.columnDefs}
        rowData={gridData}
        animateRows={true}
        domLayout="autoHeight"
        rowSelection={false}
        suppressRowHoverHighlight={true}
        getRowHeight={() => 24}
        getRowClass={(params) =>
          [
            params.data?.queueOrder > 0 ? "depth-row" : null,
            params.data?.isExpired ? "expired-instrument-row" : null,
            params.data?.showMarketDivider ? "market-group-start" : null,
          ]
            .filter(Boolean)
            .join(" ") || undefined
        }
        isExternalFilterPresent={this.isFilterActive}
        doesExternalFilterPass={this.doesExternalFilterPass}
        onGridReady={(params) => (this.gridApi = params.api)}
      />
    );
  }
}

export default withTranslation(["trading"])(MarketGrid);
